import React, { useEffect, useMemo, useRef, useState } from "react";
import Matter from "matter-js";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Stack,
  Link
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { getSetting } from "../services/api/settingApi";
import { getAnimationImage } from "../services/api/homeApi";


// const PACKS = [
//   { img: "/images/Cool Splash.png" },
//   { img: "/images/Cool Splash.png" },
//   { img: "/images/HERBAL TOUCH.png" },
//   { img: "/images/HERBAL TOUCH.png" },
//   { img: "/images/HERBAL TOUCH.png" },
//   { img: "/images/MAKEUP REMOVER.png" },
//   { img: "/images/MAKEUP REMOVER.png" },
//   { img: "/images/MAKEUP REMOVER.png" },
//   { img: "/images/REFRESHING WET WIPE.png" },
//   { img: "/images/REFRESHING WET WIPE.png" },
//   { img: "/images/WET WIPE.png" },
//   { img: "/images/WET WIPE.png" },
// ];

const menuItems = [
  { label: "Products", path: "/product" },
  { label: "Advice & Care", path: "/advice-care" },
  { label: "Contact Us", path: "/contactus" },
];

export default function Footer() {
  const wrapRef = useRef(null);
  const hasStartedRef = useRef(false);
  const stageRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  // const textures = useMemo(() => PACKS, []);
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [footerImages, setFooterImages] = useState([]);
  const textures = useMemo(() => {
    return footerImages.map((item) => ({
      img: item.image,
    }));
  }, [footerImages]);
  useEffect(() => {
    const fetchFooterImages = async () => {
      try {
        const data = await getAnimationImage();
        setFooterImages(data || []);
      } catch (err) {
        console.error("Footer images fetch error:", err);
      }
    };

    fetchFooterImages();
  }, []);
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSetting();
        setSettings(data);
      } catch (err) {
        console.error("Settings fetch error:", err);
      }
    };
    fetchSettings();
  }, []);

  const socialLinks = [
    { icon: <FacebookIcon />, label: "Facebook", url: settings?.facebook || null },
    { icon: <LinkedInIcon />, label: "Linkedin", url: settings?.tiktok || null },
    { icon: <YouTubeIcon />, label: "YouTube", url: settings?.youtube || null },
    { icon: <InstagramIcon />, label: "Instagram", url: settings?.instagram || null },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStartedRef.current) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    if (stageRef.current) observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const { Engine, Render, Runner, Bodies, Composite, Mouse, MouseConstraint } = Matter;
    const wrap = wrapRef.current;

    // if (!wrap || hasStartedRef.current || !isVisible) return;
    if (!wrap || hasStartedRef.current || !isVisible || textures.length === 0) return;
    hasStartedRef.current = true;

    const width = wrap.clientWidth;
    const height = wrap.clientHeight;

    const engine = Engine.create({ enableSleeping: false });
    engine.world.gravity.y = 0.7;

    const render = Render.create({
      element: wrap,
      engine,
      options: { width, height, background: "transparent", wireframes: false }
    });

    const isMobile = width < 768;
    const BODY_W = isMobile ? 120 : 220;
    const BODY_H = isMobile ? 85 : 150;
    const SPRITE_SCALE = isMobile ? 0.23 : 0.23;

    const packs = textures.map((p, i) => {
      const xPos = 100 + Math.random() * (width - 200);
      const yPos = -200 - i * 140;

      const texturePath = p.img && p.img.trim() !== "" ? p.img : null;

      return Bodies.rectangle(xPos, yPos, BODY_W, BODY_H, {
        chamfer: { radius: 18 },
        restitution: 0.1,
        friction: 0.5,
        render: {
          sprite: {
            texture: texturePath,
            xScale: SPRITE_SCALE,
            yScale: SPRITE_SCALE
          }
        },
      });
    });

    const floor = Bodies.rectangle(width / 2, height + 30, width, 60, { isStatic: true, render: { visible: false } });
    const leftWall = Bodies.rectangle(-30, height / 2, 60, height, { isStatic: true, render: { visible: false } });
    const rightWall = Bodies.rectangle(width + 30, height / 2, 60, height, { isStatic: true, render: { visible: false } });

    Composite.add(engine.world, [floor, leftWall, rightWall, ...packs]);

    const mouse = Mouse.create(render.canvas);
    mouse.element.removeEventListener("wheel", mouse.mousewheel);
    mouse.element.removeEventListener("DOMMouseScroll", mouse.mousewheel);

    const mouseConstraint = MouseConstraint.create(engine, {
      mouse,
      constraint: { stiffness: 0.2, render: { visible: false } },
    });

    Composite.add(engine.world, mouseConstraint);
    Render.run(render);
    Runner.run(Runner.create(), engine);

    return () => {
      Render.stop(render);
      Composite.clear(engine.world);
      Engine.clear(engine);
      render.canvas.remove();
    };
  }, [textures, isVisible]);

  return (
    <Box
      sx={{
        backgroundColor: "primary.main",
        color: "primary.contrastText",
        position: "relative",
        overflow: "hidden",
        minHeight: { xs: "130vh", md: 800 },
        touchAction: "pan-y"
      }}
    >
      {/* PHYSICS LAYER */}
      <Box
        ref={stageRef}
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 5,
          pointerEvents: "auto"
        }}
      >
        <Box ref={wrapRef} sx={{ width: "100%", height: "100%" }} />
      </Box>

      {/* CONTENT LAYER */}
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 6, md: 8 },
          px: { xs: 2, md: 10 },
          position: "relative",
          zIndex: 10,
          pointerEvents: "none"
        }}
      >
        <Grid container spacing={{ xs: 4, md: 4 }} justifyContent="space-between">

          {/* LOGO SECTION */}
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              pointerEvents: "auto",
              textAlign: { xs: "center", md: "left" }
            }}
          >

            {/* LOGO 2 */}
            {settings?.logo2 && (
              <Box
                component="img"
                src={settings.logo2}
                sx={{
                  width: { xs: 120, md: 150 },
                  cursor: "pointer",
                  display: "block",
                  mx: { xs: "auto", md: 0 }
                }}
                onClick={() => navigate("/")}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: { xs: 2, sm: 2 },
                px: { xs: 1, sm: 0 },
                pointerEvents: "none"
              }}
            >
              {socialLinks.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    bgcolor: "background.default",
                    color: "primary.main",
                    borderRadius: 4,
                    width: { xs: "46%", sm: 100 },
                    height: { xs: 125, sm: 100 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "auto",
                    cursor: "pointer",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                    transition: "0.3s",
                    "&:hover": { transform: "translateY(-4px)" }
                  }}
                  onClick={() => item.url && window.open(item.url, "_blank")}
                >
                  <IconButton color="inherit" sx={{ p: 1 }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: { xs: 42, sm: 40 } } })}
                  </IconButton>
                  <Typography
                    fontSize={{ xs: 13, sm: 12 }}
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* MENU SECTION */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={4} sx={{ pointerEvents: "auto", }}>
              <Grid item xs={6}>
                <Typography fontWeight={800} mb={2} sx={{ fontSize: '1.1rem' }}>Products & Care</Typography>
                {menuItems.map((item) => (
                  <Typography
                    key={item.label}
                    sx={{ cursor: "pointer", mb: 1.5, fontSize: '0.95rem', opacity: 0.9 }}
                    onClick={() => navigate(item.path)}
                  >
                    {item.label}
                  </Typography>
                ))}
              </Grid>
              <Grid item xs={6}>
                <Typography fontWeight={800} mb={2} sx={{ fontSize: '1.1rem' }}>Company</Typography>
                {["Our Story", "Environment", "People", "Leadership Team", "Contact"].map((t) => (
                  <Typography key={t} sx={{ mb: 1.5, fontSize: '0.95rem', opacity: 0.9 }}>{t}</Typography>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* BOTTOM COPYRIGHT */}
          <Grid item xs={12} sx={{ mt: { xs: 2, md: -4 }, pointerEvents: "auto" }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 500 }}>© 2026 Puriff Hygiene</Typography>
              <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                <Link component={RouterLink} to="/privacypolicy" sx={{ color: "#fff", textDecoration: "none", fontSize: '0.9rem' }}>Privacy Policy</Link>
                <Link component={RouterLink} to="/cookie-policy" sx={{ color: "#fff", textDecoration: "none", fontSize: '0.9rem' }}>Cookie Policy</Link>
                <Link component={RouterLink} to="/termsCondition" sx={{ color: "#fff", textDecoration: "none", fontSize: '0.9rem' }}>Terms & Conditions</Link>
              </Stack>
            </Stack>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
}